import { getWebOrigin } from '@lib/platform';

/**
 * The one place a shareable app URL gets its `?source=` attribution — and the one place
 * that decides WHERE in the query string it goes.
 *
 * Two surfaces build the same Space link from a path that may already carry
 * `?invite=<token>`: `buildSpaceInviteLink` on the organizer's invite screen, and the share
 * sheet's `qrTarget`. Appending `source` to whatever the path carried yields
 * `?invite=TOK&source=qr_invite` on one and `?source=qr_invite&invite=TOK` on the other:
 * one destination, two strings. For a QR that is not cosmetic — `useQrSvg` caches on
 * the link (`qrSvgKey`) and the encoder renders the literal bytes, so the drift costs a
 * second cache entry and a visibly different QR image for the same
 * Space. `source` is written first and the rest of the query follows in the order the path
 * carried it, so the two surfaces cannot drift apart again.
 *
 * Uses `getWebOrigin()`, not `window.location.origin`: on Capacitor the latter is the
 * internal WebView scheme, which would hand out a link nobody outside the app can open.
 *
 * Not `getShareUrl` (`@utils/shareHelpers`): that one adds `ref`/`ref_user` and feeds the
 * `go.meetball.fun` shortener, neither of which a QR wants.
 */
export function buildSourceTaggedUrl(path: string, source: string): string {
  const url = new URL(`${getWebOrigin()}${path}`);
  // A `source` already on the path is replaced rather than duplicated: the caller's is the
  // channel actually handing this link over.
  const carried = [...url.searchParams].filter(([key]) => key !== 'source');
  url.search = '';
  url.searchParams.set('source', source);
  for (const [key, value] of carried) {
    url.searchParams.append(key, value);
  }
  return url.toString();
}
